import EmailIcon from '@mui/icons-material/Email';
import { Box, Checkbox, FormControlLabel, Grid } from '@mui/material';
import { DocumentType } from '@prisma/client';
import { Formik } from 'formik';
import * as yup from 'yup';

import { Card } from 'src/components';
import Button from 'src/components/Button';
import { RadioGroupField } from 'src/components/common/form/FormRadioGroup';
import { DropDown, FieldInput, NPIAutoComplete } from 'src/components/FormikComponents';
import { InputWithSearch } from 'src/components/InputWithSearch';
import { ConfirmationModal } from 'src/components/Modals';
import { DebitFor, parsePlatform, Platform } from 'src/constants/direct-debit';
import useSendSepaDD from 'src/features/finance/sepaDirectDebits/hooks/useSendSepaDD';
import { useValidateEmail } from 'src/hooks';
import useMerchantsAndPartners from 'src/hooks/useMerchantsAndPartners';
import MerchantSelectFields from 'src/pages/AddSalesInvoicePage/components/MerchantSelectFields';
import { ContactInfo } from 'src/types/direct-debit';

const getDirectDebitValidationSchema = (isUsingExistingBankDetails: boolean) => {
    return yup.object().shape({
        firstName: isUsingExistingBankDetails
            ? yup.string() // No validation if using existing bank details
            : yup.string().required('First Name is required'),
        lastName: isUsingExistingBankDetails
            ? yup.string() // No validation if using existing bank details
            : yup.string().required('Last Name is required'),
        emailAddress: isUsingExistingBankDetails
            ? yup.string() // No validation if using existing bank details
            : yup.string().required('Enter a valid Email Address'),
        typeOfPayment: yup.string().required('Type of Payment is required'),
        partnerId: yup
            .string()
            .nullable()
            .when(['debitFor', 'platform'], {
                is: (debitFor, platform) => {
                    return debitFor === DebitFor.PARTNER && platform === Platform.BUDDY;
                },
                then: (schema) => {
                    return schema.required();
                },
                otherwise: (schema) => {
                    return schema.optional();
                },
            }),
        merchantId: yup
            .string()
            .nullable()
            .when(['debitFor', 'platform'], {
                is: (debitFor, platform) => {
                    return debitFor === DebitFor.MERCHANT && platform === Platform.BUDDY;
                },
                then: (schema) => {
                    return schema.required();
                },
                otherwise: (schema) => {
                    return schema.optional();
                },
            }),
    });
};

const typesOfPayments = [
    {
        value: 'RECURRING',
        label: 'Recurring',
    },
    {
        value: 'ONE_OFF',
        label: 'One-Off',
    },
];

export function getDisableUseBankCheckbox({
    platform,
    directDebitFor,
    values,
    merchantContactInfo,
    partnerContactInfo,
}: {
    platform: Platform;
    directDebitFor: DebitFor;
    values: SepaDDFormValues;
    merchantContactInfo: ContactInfo;
    partnerContactInfo: ContactInfo;
}) {
    if (platform === Platform.OTHER) {
        return true;
    }

    if (directDebitFor === DebitFor.MERCHANT && (!values.merchantId || !merchantContactInfo)) {
        return true;
    }

    if (directDebitFor === DebitFor.PARTNER && (!partnerContactInfo || !values.partnerId)) {
        return true;
    }

    return false;
}

export type SepaDDFormValues = {
    firstName: string;
    lastName: string;
    emailAddress: string;
    typeOfPayment: string;
    partnerId?: string;
    merchantId?: string;
    platform: Platform;
    debitFor: DebitFor;
};

export type SepaDDFormProps = {
    onSendSepaDD: () => void;
};

const SepaDDForm = ({ onSendSepaDD }: SepaDDFormProps) => {
    const {
        partnerOptions,
        merchantOptions,
        partnersData,
        merchantsData,
        merchantAccountNumberOptions,
    } = useMerchantsAndPartners();

    const { onValidateEmail } = useValidateEmail();

    const {
        isOpen,
        debitData,
        isSubmiting,
        directDebitFor,
        isSubmitted,
        isUsingExistingBankDetails,
        sendMandate,
        handleClose,
        bankChecked,
        setDirectDebitFor,
        platform,
        setPlatform,
        displayDirectDebitModal,
        setIsSubmitted,
        setIsUsingExistingBankDetails,
        clearExistingContactInfo,
        merchantSelected,
        partnerSelected,
        resetFields,
        selectedMerchantAccountNumberOption,
        setSelectedMerchantAccountNumberOption,
        selectedMerchantOption,
        setSelectedMerchantOption,
        setPartner,
        merchantContactInfo,
        setMerchantContactInfo,
        partner,
        partnerContactInfo,
        setPartnerContactInfo,
    } = useSendSepaDD({ onSendSepaDD, onValidateEmail, partnersData, merchantsData });

    const handleClearMerchantInputs = (setFieldValue, resetForm, values) => {
        setSelectedMerchantAccountNumberOption(null);
        setSelectedMerchantOption(null);
        setMerchantContactInfo(null);
        clearExistingContactInfo(
            setFieldValue,
            resetForm,
            values.directDebitFor,
            values.platform,
            setIsUsingExistingBankDetails
        );
    };

    return (
        <>
            <Formik<SepaDDFormValues>
                initialValues={{
                    firstName: '',
                    lastName: '',
                    emailAddress: '',
                    typeOfPayment: 'RECURRING',
                    partnerId: null,
                    merchantId: null,
                    platform: Platform.BUDDY,
                    debitFor: DebitFor.MERCHANT,
                }}
                validationSchema={getDirectDebitValidationSchema(isUsingExistingBankDetails)}
                validateOnChange
                enableReinitialize
                onSubmit={displayDirectDebitModal}
            >
                {({
                    values,
                    setFieldValue,
                    setFieldError,
                    handleSubmit,
                    resetForm,
                    initialValues,
                    isValid,
                    errors,
                    setErrors,
                }) => {
                    return (
                        <>
                            <Card>
                                {(() => {
                                    /**  When platform buddy, and merchant/partner selected, clicking use bank first time
                             populates the values correctly but the validate is behind
                             this handles that edge case by running validation again and setting errors manually
                             when the validation succeeds */
                                    if (!isValid) {
                                        let shouldRevalidate = false;
                                        if (values.firstName.length > 0 && errors.firstName) {
                                            shouldRevalidate = true;
                                        }
                                        if (values.lastName.length > 0 && errors.lastName) {
                                            shouldRevalidate = true;
                                        }
                                        if (values.emailAddress.length > 0 && errors.emailAddress) {
                                            shouldRevalidate = true;
                                        }
                                        if (shouldRevalidate) {
                                            getDirectDebitValidationSchema(
                                                isUsingExistingBankDetails
                                            )
                                                .validate(values)
                                                .then((res) => {
                                                    setErrors({});
                                                });
                                        }
                                    }
                                })()}
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <RadioGroupField
                                            label="New SEPA Direct Debit for?"
                                            containerGridSpacing={3}
                                            labelGridSize={3}
                                            radioGroupGridSize={6}
                                            radioOptionGridSize={6}
                                            setselectedvalue={(value: string) => {
                                                if (value) {
                                                    const valueFor = value as DebitFor;
                                                    resetFields(
                                                        setFieldValue,
                                                        resetForm,
                                                        valueFor,
                                                        platform
                                                    );
                                                    setDirectDebitFor(valueFor);
                                                    setFieldValue('debitFor', valueFor);
                                                }
                                            }}
                                            name="directDebitFor"
                                            defaultValue={initialValues.debitFor}
                                            options={[
                                                { label: 'Merchant', value: DebitFor.MERCHANT },
                                                { label: 'Partner', value: DebitFor.PARTNER },
                                            ]}
                                        />
                                        <RadioGroupField
                                            label="Which Platform?"
                                            containerGridSpacing={3}
                                            labelGridSize={3}
                                            radioGroupGridSize={6}
                                            radioOptionGridSize={6}
                                            setselectedvalue={(value: string) => {
                                                if (value) {
                                                    const platform = parsePlatform(value);
                                                    resetFields(
                                                        setFieldValue,
                                                        resetForm,
                                                        values.debitFor,
                                                        platform
                                                    );

                                                    setPlatform(platform);
                                                }
                                            }}
                                            name="platform"
                                            defaultValue={initialValues.platform}
                                            options={[
                                                { label: 'buddy', value: Platform.BUDDY },
                                                { label: 'Other', value: Platform.OTHER },
                                            ]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box className="mt-20">
                                            {directDebitFor == DebitFor.PARTNER ? (
                                                <NPIAutoComplete
                                                    dataTestId="partner-autocomplete"
                                                    header="Partner"
                                                    name="partner"
                                                    options={partnerOptions}
                                                    placeholder="Select Partner"
                                                    required={
                                                        isSubmitted && platform !== Platform.OTHER
                                                    }
                                                    onChange={(value) => {
                                                        return partnerSelected(
                                                            value,
                                                            setFieldValue,
                                                            isUsingExistingBankDetails
                                                        );
                                                    }}
                                                    onClear={() => {
                                                        setPartner(null);
                                                        setPartnerContactInfo(null);
                                                        clearExistingContactInfo(
                                                            setFieldValue,
                                                            resetForm,
                                                            values.directDebitFor,
                                                            values.platform,
                                                            setIsUsingExistingBankDetails
                                                        );
                                                    }}
                                                    controlledValue={partner}
                                                    setSelectedValue={setPartner}
                                                    useFormik={false}
                                                    disabled={platform === Platform.OTHER}
                                                />
                                            ) : (
                                                <MerchantSelectFields
                                                    merchant={selectedMerchantOption}
                                                    merchantAccountNumber={
                                                        selectedMerchantAccountNumberOption
                                                    }
                                                    disabled={platform === Platform.OTHER}
                                                    merchantOptions={merchantOptions}
                                                    merchantAccountNumberOptions={
                                                        merchantAccountNumberOptions
                                                    }
                                                    handleChangeMerchant={(newValue, reason) => {
                                                        if (reason === 'clear') {
                                                            handleClearMerchantInputs(
                                                                setFieldValue,
                                                                resetForm,
                                                                values
                                                            );
                                                        } else if (reason === 'selectOption') {
                                                            merchantSelected(
                                                                newValue,
                                                                setFieldValue,
                                                                isUsingExistingBankDetails
                                                            );
                                                        }
                                                    }}
                                                    handleChangeMerchantAccountNumber={(
                                                        newValue,
                                                        reason
                                                    ) => {
                                                        if (reason === 'clear') {
                                                            handleClearMerchantInputs(
                                                                setFieldValue,
                                                                resetForm,
                                                                values
                                                            );
                                                        } else if (reason === 'selectOption') {
                                                            const selectedMerchant =
                                                                merchantOptions.find(
                                                                    (merchantOption) => {
                                                                        return (
                                                                            newValue?.value ===
                                                                            merchantOption?.id
                                                                        );
                                                                    }
                                                                );

                                                            merchantSelected(
                                                                selectedMerchant,
                                                                setFieldValue,
                                                                isUsingExistingBankDetails
                                                            );
                                                        }
                                                    }}
                                                />
                                            )}
                                            <Box>
                                                <FormControlLabel
                                                    label="Use Existing Bank Details?"
                                                    control={
                                                        <Checkbox
                                                            data-testid="use-bank-details-checkbox"
                                                            name="useBankDetails"
                                                            checked={isUsingExistingBankDetails}
                                                            onChange={(event) => {
                                                                bankChecked(
                                                                    event,
                                                                    setFieldValue,
                                                                    directDebitFor ===
                                                                        DebitFor.MERCHANT
                                                                        ? merchantContactInfo
                                                                        : partnerContactInfo
                                                                );
                                                            }}
                                                            disabled={getDisableUseBankCheckbox({
                                                                directDebitFor,
                                                                platform,
                                                                values,
                                                                merchantContactInfo,
                                                                partnerContactInfo,
                                                            })}
                                                        />
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FieldInput
                                            label="First Name"
                                            name="firstName"
                                            placeholder="Enter First Name"
                                            disabled={isUsingExistingBankDetails}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FieldInput
                                            label="Last Name"
                                            name="lastName"
                                            placeholder="Enter Last Name"
                                            disabled={isUsingExistingBankDetails}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <InputWithSearch
                                            fieldName="emailAddress"
                                            label="Email Address"
                                            placeholder="Enter Email Address"
                                            searchText="Validate"
                                            disabled={isUsingExistingBankDetails}
                                            onSearch={async (email) => {
                                                return await onValidateEmail(
                                                    email ?? '',
                                                    () => {
                                                        return setFieldError(
                                                            'emailAddress',
                                                            'Provide a valid email address'
                                                        );
                                                    },
                                                    () => {
                                                        return setFieldError('emailAddress', null);
                                                    }
                                                );
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <DropDown
                                            name="typeOfPayment"
                                            label="Type of Payment?"
                                            placeholder="Please Select Type of Payment"
                                            options={typesOfPayments}
                                            defaultValue={initialValues.typeOfPayment}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12} textAlign="right">
                                        <Button
                                            disabled={!isValid}
                                            dataTestId="send-sepa-dd-button"
                                            startIcon={<EmailIcon />}
                                            variant="npi-primary"
                                            loading={isSubmiting}
                                            onClick={() => {
                                                setIsSubmitted(true);
                                                handleSubmit();
                                            }}
                                        >
                                            {directDebitFor === DebitFor.PARTNER
                                                ? 'Send Email To Partner'
                                                : 'Send Email To Merchant'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Card>
                            <ConfirmationModal
                                isOpen={isOpen}
                                onConfirm={() => {
                                    sendMandate();
                                    handleClose();
                                }}
                                name={'send-sepa-mandate-confirmation-modal'}
                                confirmBtnVariant="npi-primary"
                                confirmBtnWdith="auto"
                                confirmText="Send SEPA DD Mandate"
                                onCancel={handleClose}
                                title="Send SEPA DD Mandate"
                                modalWidth="40%"
                                message={
                                    <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            {debitData?.documentType ===
                                            DocumentType.MERCHANT_SEPA_DD
                                                ? 'Merchant'
                                                : 'Partner'}{' '}
                                            will receive a new SEPA DD Mandate...
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            {values.firstName} {values.lastName} - (
                                            {values.emailAddress})
                                        </Box>
                                    </Box>
                                }
                                hideCancelButton
                            />
                        </>
                    );
                }}
            </Formik>
        </>
    );
};

export default SepaDDForm;
